import React, { useMemo, useEffect, useState } from 'react';
import { diffLines, diffWords, Change } from 'diff';

interface MobileDiffViewerProps {
    oldContent: string;
    newContent: string;
    showDiffOnly?: boolean;
}

const MobileDiffViewer: React.FC<MobileDiffViewerProps> = ({ oldContent, newContent, showDiffOnly = true }) => {
    const [expandedFolds, setExpandedFolds] = useState<Set<number>>(new Set());

    // Reset folds when content changes
    useEffect(() => {
        setExpandedFolds(new Set());
    }, [oldContent, newContent, showDiffOnly]);

    const { lines } = useMemo(() => {
        const lineChanges = diffLines(oldContent, newContent);
        const processedLines: {
            type: 'added' | 'removed' | 'common' | 'modified-part';
            lineNumber?: number;
            content: React.ReactNode;
            raw: string;
        }[] = [];

        let leftLineNumber = 1;
        let rightLineNumber = 1;

        for (let i = 0; i < lineChanges.length; i++) {
            const part = lineChanges[i];
            const nextPart = lineChanges[i + 1];

            // Handle block replacements (remove followed by add) as sequential lines with word diffs
            if (part.removed && nextPart && nextPart.added) {
                const removedLines = part.value.replace(/\n$/, '').split('\n');
                const addedLines = nextPart.value.replace(/\n$/, '').split('\n');
                const maxLength = Math.max(removedLines.length, addedLines.length);

                // We need to process all lines in both blocks. 
                // For visual alignment in "unified" view, we simply output all removed lines then all added lines.
                // BUT we compute word diffs using index matching (line 1 of removed vs line 1 of added) to give context.

                // First, process all REMOVED lines
                removedLines.forEach((leftContent, index) => {
                    const rightContent = addedLines[index] || '';
                    const wordChanges = diffWords(leftContent, rightContent);

                    // For removed line, show parts that are NOT added. Highlight Removed.
                    const content = wordChanges.filter(p => !p.added).map((p, idx) => (
                        <span key={idx} className={p.removed ? 'bg-red-200' : ''}>{p.value}</span>
                    ));

                    processedLines.push({
                        type: 'removed',
                        lineNumber: leftLineNumber++,
                        content: content,
                        raw: leftContent
                    });
                });

                // Then, process all ADDED lines
                addedLines.forEach((rightContent, index) => {
                    const leftContent = removedLines[index] || '';
                    const wordChanges = diffWords(leftContent, rightContent);

                    // For added line, show parts that are NOT removed. Highlight Added.
                    const content = wordChanges.filter(p => !p.removed).map((p, idx) => (
                        <span key={idx} className={p.added ? 'bg-green-200' : ''}>{p.value}</span>
                    ));

                    processedLines.push({
                        type: 'added',
                        lineNumber: rightLineNumber++,
                        content: content,
                        raw: rightContent
                    });
                });

                i++; // Skip next part
            } else {
                const lines = part.value.replace(/\n$/, '').split('\n');
                lines.forEach((line: string) => {
                    if (part.added) {
                        processedLines.push({
                            type: 'added',
                            lineNumber: rightLineNumber++,
                            content: line,
                            raw: line
                        });
                    } else if (part.removed) {
                        processedLines.push({
                            type: 'removed',
                            lineNumber: leftLineNumber++,
                            content: line,
                            raw: line
                        });
                    } else {
                        processedLines.push({
                            type: 'common',
                            lineNumber: rightLineNumber++,
                            content: line,
                            raw: line
                        });
                        leftLineNumber++;
                    }
                });
            }
        }

        return { lines: processedLines };
    }, [oldContent, newContent]);

    const renderedLines: React.ReactNode[] = [];
    let commonLineBuffer: typeof lines = [];

    const flushCommonLineBuffer = (isExpanded: boolean) => {
        if (commonLineBuffer.length > 0) {
            if (!isExpanded) {
                const foldIndex = renderedLines.length;
                renderedLines.push(
                    <div key={`fold-${foldIndex}`}
                        className="py-2 px-4 bg-gray-50 text-gray-500 text-center text-xs border-y border-gray-100 cursor-pointer hover:bg-gray-100"
                        onClick={() => setExpandedFolds(prev => new Set(prev).add(foldIndex))}>
                        ... Show {commonLineBuffer.length} unchanged lines ...
                    </div>
                );
            } else {
                commonLineBuffer.forEach((line, i) => renderedLines.push(renderLine(line, `common-${renderedLines.length}-${i}`)));
            }
            commonLineBuffer = [];
        }
    };

    const renderLine = (line: (typeof lines)[0], key: string) => {
        let bgColor = 'bg-transparent';
        if (line.type === 'removed') bgColor = 'bg-red-50';
        if (line.type === 'added') bgColor = 'bg-green-50';

        let textColor = 'text-gray-800';
        if (line.type === 'common') textColor = 'text-gray-500';

        const indicator = line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' ';
        const indicatorColor = line.type === 'added' ? 'text-green-600' : line.type === 'removed' ? 'text-red-600' : 'text-gray-400';

        return (
            <div key={key} className={`flex text-sm font-mono ${bgColor} border-b border-gray-100 last:border-0`}>
                <div className="w-8 flex-shrink-0 text-right pr-2 py-1 text-gray-400 select-none border-r border-gray-200 bg-gray-50 text-xs">
                    {line.lineNumber}
                </div>
                <div className={`w-6 flex-shrink-0 text-center py-1 select-none font-bold ${indicatorColor}`}>
                    {indicator}
                </div>
                <div className={`flex-grow py-1 px-2 whitespace-pre-wrap break-all ${textColor}`}>
                    {line.content || ' '}
                </div>
            </div>
        );
    };

    lines.forEach((line, index) => {
        if (showDiffOnly && line.type === 'common') {
            commonLineBuffer.push(line);
        } else {
            flushCommonLineBuffer(expandedFolds.has(renderedLines.length));
            renderedLines.push(renderLine(line, `line-${index}`));
        }
    });
    flushCommonLineBuffer(expandedFolds.has(renderedLines.length));

    return (
        <div className="flex flex-col w-full h-full overflow-auto bg-white">
            {renderedLines}
        </div>
    );
};

export default MobileDiffViewer;
